import React, { useState, useMemo } from 'react';
import { ApprovalRequest } from '../../types';
import { Clock, AlertCircle, CheckCircle2, GitPullRequest, Wrench, AlertTriangle } from 'lucide-react';

interface ApprovalQueueProps {
  approvals: ApprovalRequest[];
  selectedApproval: ApprovalRequest | null;
  onApprovalSelect: (approval: ApprovalRequest) => void;
  className?: string;
}

const ApprovalQueue: React.FC<ApprovalQueueProps> = ({
  approvals,
  selectedApproval,
  onApprovalSelect,
  className = ''
}) => {
  const [filter, setFilter] = useState<'all' | 'code_review' | 'deployment' | 'manual_intervention'>('all');
  const [sortBy, setSortBy] = useState<'priority' | 'waitingTime' | 'createdAt'>('priority');

  // Filter and sort approvals
  const filteredAndSortedApprovals = useMemo(() => {
    let filtered = approvals;
    
    // Apply filter
    if (filter !== 'all') {
      filtered = filtered.filter(approval => approval.type === filter);
    }
    
    // Apply sorting
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'priority':
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        case 'waitingTime':
          return b.waitingTime - a.waitingTime;
        case 'createdAt':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        default:
          return 0;
      }
    });
  }, [approvals, filter, sortBy]);

  const getTypeIcon = (type: ApprovalRequest['type']) => {
    switch (type) {
      case 'code_review':
        return <GitPullRequest className="w-4 h-4" />;
      case 'deployment':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'manual_intervention':
        return <Wrench className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type: ApprovalRequest['type']) => {
    switch (type) {
      case 'code_review':
        return 'Code Review';
      case 'deployment':
        return 'Deployment';
      case 'manual_intervention':
        return 'Manual Intervention';
      default:
        return 'Unknown';
    }
  };

  const getPriorityColor = (priority: ApprovalRequest['priority']) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatWaitingTime = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes}m`;
    } else if (minutes < 1440) {
      return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
    } else {
      const days = Math.floor(minutes / 1440);
      const hours = Math.floor((minutes % 1440) / 60);
      return `${days}d ${hours}h`;
    }
  };

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Filters and Sort */}
      <div className="p-4 border-b bg-white space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Filter by Type</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Types</option>
            <option value="code_review">Code Review</option>
            <option value="deployment">Deployment</option>
            <option value="manual_intervention">Manual Intervention</option>
          </select>
        </div>
        
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Sort by</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="priority">Priority</option>
            <option value="waitingTime">Waiting Time</option>
            <option value="createdAt">Created Date</option>
          </select>
        </div>
      </div>

      {/* Approval List */}
      <div className="flex-1 overflow-y-auto">
        {filteredAndSortedApprovals.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm">No approvals match the current filter</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredAndSortedApprovals.map((approval) => (
              <div
                key={approval.id}
                onClick={() => onApprovalSelect(approval)}
                className={`p-4 cursor-pointer transition-colors ${
                  selectedApproval?.id === approval.id
                    ? 'bg-blue-50 border-r-4 border-blue-500'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="text-gray-500">
                      {getTypeIcon(approval.type)}
                    </div>
                    <span className="text-xs font-medium text-gray-600">
                      {getTypeLabel(approval.type)}
                    </span>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full border font-medium ${getPriorityColor(approval.priority)}`}>
                    {approval.priority}
                  </span>
                </div>

                <h3 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2">
                  {approval.title}
                </h3>
                
                <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                  {approval.description}
                </p>

                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center space-x-1">
                    <Clock className="w-3 h-3" />
                    <span>Waiting {formatWaitingTime(approval.waitingTime)}</span>
                  </div>
                  
                  {approval.context.jiraTicket && (
                    <span className="font-mono bg-gray-100 px-1 rounded">
                      {approval.context.jiraTicket}
                    </span>
                  )}
                </div>

                {approval.context.prNumber && (
                  <div className="mt-2 text-xs text-blue-600">
                    PR #{approval.context.prNumber}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalQueue;
